/* WrittenStoriesD.jsx
 *
 * OPTION D — Themed bands + pull-quote mosaic per band
 *
 * Combines B's structure (grouped strips by motivation) with A's visual
 * language (varied-size pull-quote cards in cream/white/paper tones).
 * Each band has its own dominant background colour, and within the band
 * the cards alternate sizes and tones for visual rhythm.
 *
 * Critically: card patterns are tuned per band-size so 2-, 3-, 4-, 5- and
 * 6-card bands all read as intentional layouts — never sparse.
 */
import { useState, useEffect } from 'react';
import { LEARNER_STORIES } from './LearnerStoriesData.jsx';

const D_THEMES = [
  {
    id: 'family',
    label: 'Family ties',
    intro: 'Grandparents, parents, partners, children — most of our learners start because of someone they love.',
    bg: '#FBF3EF',
    accent: '#C2130C',
  },
  {
    id: 'heritage',
    label: 'Heritage rediscovered',
    intro: 'A name on a census, a DNA result, a holiday that turned into a homecoming.',
    bg: '#F5EFE7',
    accent: '#C2130C',
  },
  {
    id: 'livingInWales',
    label: 'Living in Wales',
    intro: 'New arrivals who decided that learning the language was the right thing to do.',
    bg: '#EFEEE5',
    accent: '#C2130C',
  },
  {
    id: 'returning',
    label: 'Coming back to it',
    intro: 'School Welsh that never quite stuck, picked up again decades later.',
    bg: '#F2EBE3',
    accent: '#C2130C',
  },
  {
    id: 'loveOfLanguage',
    label: 'For the love of the language',
    intro: 'No ancestry, no obligation — just learners who fell for the sound of it.',
    bg: '#EFE7E1',
    accent: '#C2130C',
  },
  {
    id: 'unexpected',
    label: 'The unexpected ones',
    intro: 'A bet, a dream, a lorry-cab radio, a TV invitation. Not every story starts the way you might expect.',
    bg: '#F0E8E1',
    accent: '#C2130C',
  },
];
// Tones are: 'white', 'cream', 'red'. 'red' = brand accent card with white text.
// Sizes: sm / md / lg / wide.
const D_PATTERNS = {
  1: [
    { col: 'span 12', tone: 'red',   size: 'wide' },
  ],
  2: [
    { col: 'span 7',  tone: 'red',   size: 'lg' },
    { col: 'span 5',  tone: 'white', size: 'md' },
  ],
  3: [
    { col: 'span 5',  tone: 'white', size: 'md' },
    { col: 'span 4',  tone: 'red',   size: 'md' },
    { col: 'span 3',  tone: 'cream', size: 'sm' },
  ],
  4: [
    { col: 'span 5',  tone: 'red',   size: 'lg' },
    { col: 'span 4',  tone: 'white', size: 'md' },
    { col: 'span 3',  tone: 'cream', size: 'sm' },
    { col: 'span 12', tone: 'white', size: 'wide' },
  ],
  5: [
    { col: 'span 5',  tone: 'cream', size: 'lg' },
    { col: 'span 4',  tone: 'white', size: 'md' },
    { col: 'span 3',  tone: 'red',   size: 'sm' },
    { col: 'span 7',  tone: 'white', size: 'md' },
    { col: 'span 5',  tone: 'cream', size: 'md' },
  ],
  6: [
    { col: 'span 4',  tone: 'white', size: 'md' },
    { col: 'span 5',  tone: 'red',   size: 'lg' },
    { col: 'span 3',  tone: 'cream', size: 'sm' },
    { col: 'span 5',  tone: 'cream', size: 'md' },
    { col: 'span 3',  tone: 'white', size: 'sm' },
    { col: 'span 4',  tone: 'red',   size: 'md' },
  ],
  // For 7+ we use a cycle of 8 sub-patterns chosen for visual variety.
  // patternForBand picks from this when count > 6.
  cycle: [
    { col: 'span 5',  tone: 'cream', size: 'lg' },
    { col: 'span 4',  tone: 'white', size: 'md' },
    { col: 'span 3',  tone: 'red',   size: 'sm' },
    { col: 'span 4',  tone: 'cream', size: 'md' },
    { col: 'span 4',  tone: 'red',   size: 'md' },
    { col: 'span 4',  tone: 'white', size: 'md' },
    { col: 'span 7',  tone: 'red',   size: 'lg' },
    { col: 'span 5',  tone: 'cream', size: 'md' },
    { col: 'span 3',  tone: 'white', size: 'sm' },
    { col: 'span 5',  tone: 'red',   size: 'md' },
    { col: 'span 4',  tone: 'cream', size: 'md' },
    { col: 'span 8',  tone: 'white', size: 'wide' },
    { col: 'span 4',  tone: 'red',   size: 'md' },
    { col: 'span 5',  tone: 'cream', size: 'lg' },
    { col: 'span 3',  tone: 'white', size: 'sm' },
    { col: 'span 4',  tone: 'red',   size: 'md' },
  ],
};

// For bands of 1-6: use a tuned pattern. For 7+: cycle through a 16-step
// sequence so every card has a distinct neighbour-relationship.
function patternForBand(count) {
  if (D_PATTERNS[count]) return D_PATTERNS[count];
  const base = D_PATTERNS.cycle;
  const out = [];
  // Offset start by (count % base.length) so different-sized bands don't all
  // open the same way.
  const offset = count % 5;
  for (let i = 0; i < count; i++) out.push(base[(i + offset) % base.length]);
  return out;
}

const D_TONES = {
  white: { bg: '#FFFFFF', fg: 'var(--fg-1)', meta: 'var(--grey)', mark: 'rgba(194, 19, 12, .14)', border: '1px solid var(--border)' },
  cream: { bg: '#FFF8F2', fg: 'var(--fg-1)', meta: 'var(--grey)', mark: 'rgba(194, 19, 12, .18)', border: '1px solid var(--border)' },
  red:   { bg: 'var(--brandRed)', fg: '#fff', meta: 'rgba(255,255,255,.78)', mark: 'rgba(255,255,255,.32)', border: 'none' },
};

const D_SIZES = {
  sm:   { qSize: 17, qLine: 1.4,  pad: '26px 24px 22px', minH: 200 },
  md:   { qSize: 21, qLine: 1.34, pad: '32px 30px 26px', minH: 260 },
  lg:   { qSize: 27, qLine: 1.26, pad: '40px 38px 32px', minH: 320 },
  wide: { qSize: 30, qLine: 1.22, pad: '46px 48px 38px', minH: 240 },
};

function MosaicCard({ story, pattern, onOpen }) {
  const tone = D_TONES[pattern.tone];
  const size = D_SIZES[pattern.size];

  return (
    <article
      className="ssi-wsd__card"
      style={{
        gridColumn: pattern.col,
        background: tone.bg,
        color: tone.fg,
        padding: size.pad,
        borderRadius: 18,
        border: tone.border,
        minHeight: size.minH,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        cursor: 'pointer',
        transition: 'transform 200ms ease, box-shadow 200ms ease',
      }}
      onClick={onOpen}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 18px 40px -24px rgba(45,15,15,.28)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
    >
      <svg width="32" height="24" viewBox="0 0 38 30" aria-hidden="true" style={{ marginBottom: 14, flexShrink: 0 }}>
        <path d="M0 30V17.2C0 9.1 2.3 3.3 10.5 0l3.3 3.6c-5 2.6-7 6-7 10.6h7V30H0zm21 0V17.2C21 9.1 23.3 3.3 31.5 0l3.3 3.6c-5 2.6-7 6-7 10.6H35V30H21z" fill={tone.mark}/>
      </svg>

      <blockquote style={{
        margin: 0,
        flex: 1,
        fontFamily: 'var(--font-display)',
        fontSize: size.qSize,
        lineHeight: size.qLine,
        letterSpacing: '-.01em',
        textWrap: 'pretty',
      }}>
        "{story.pullQuote}"
      </blockquote>

      <footer style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 12,
        marginTop: 22,
        paddingTop: 16,
        borderTop: pattern.tone === 'red' ? '1px solid rgba(255,255,255,.22)' : '1px solid rgba(45, 15, 15, .12)',
        fontSize: 13,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{story.name}</div>
          <div style={{ color: tone.meta, fontSize: 12.5 }}>{story.location}</div>
        </div>
        <div style={{
          color: tone.meta,
          fontSize: 11,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          fontWeight: 600,
          flexShrink: 0,
        }}>
          Read more
        </div>
      </footer>
    </article>
  );
}

// Theme labels for the modal masthead
const D_THEME_LABELS = {
  family:         'Family ties',
  heritage:       'Heritage rediscovered',
  livingInWales:  'Living in Wales',
  returning:      'Coming back to it',
  loveOfLanguage: 'For the love of the language',
  unexpected:     'The unexpected ones',
};

function StoryModal({ story, onClose }) {
  // Lock body scroll + close on Escape
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (!story) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20, 0, 0, .82)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
        padding: 32,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative',
          width: 'min(720px, 100%)',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: -44,
            right: 0,
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: 'rgba(255,255,255,.15)',
            border: '1px solid rgba(255,255,255,.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
            color: '#fff',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
        </button>

        <div style={{
          background: '#FFFCF7',
          borderRadius: 16,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '88vh',
        }}>
          {/* Pull quote header */}
          <div style={{
            background: 'var(--brandRed)',
            color: '#fff',
            padding: '36px 40px 32px',
          }}>
            <svg width="36" height="28" viewBox="0 0 38 30" aria-hidden="true" style={{ opacity: .35, marginBottom: 14 }}>
              <path d="M0 30V17.2C0 9.1 2.3 3.3 10.5 0l3.3 3.6c-5 2.6-7 6-7 10.6h7V30H0zm21 0V17.2C21 9.1 23.3 3.3 31.5 0l3.3 3.6c-5 2.6-7 6-7 10.6H35V30H21z" fill="#fff"/>
            </svg>
            <blockquote style={{
              margin: 0,
              fontFamily: 'var(--font-display)',
              fontSize: 26,
              lineHeight: 1.28,
              letterSpacing: '-.01em',
              textWrap: 'pretty',
            }}>"{story.pullQuote}"</blockquote>
          </div>

          {/* Body — scrollable */}
          <div style={{
            padding: '28px 40px 32px',
            overflowY: 'auto',
            flex: 1,
            fontFamily: 'var(--font-body, Georgia, serif)',
            fontSize: 16.5,
            lineHeight: 1.7,
            color: 'var(--fg-2, #382020)',
          }}>
            {/* Byline */}
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 14,
              flexWrap: 'wrap',
              paddingBottom: 16,
              marginBottom: 22,
              borderBottom: '1px solid rgba(45, 15, 15, .14)',
              fontSize: 13,
            }}>
              <div style={{
                fontFamily: 'var(--font-display)',
                fontSize: 19,
                color: 'var(--fg-1)',
                letterSpacing: '-.005em',
              }}>{story.name}</div>
              <div style={{ color: 'var(--grey)' }}>{story.location}</div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'baseline', gap: 14, color: 'var(--grey)', fontSize: 12 }}>
                <span style={{
                  letterSpacing: '.12em',
                  textTransform: 'uppercase',
                  color: 'var(--brandRed)',
                  fontWeight: 700,
                }}>{D_THEME_LABELS[story.theme] || 'Story'}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{story.date}</span>
              </div>
            </div>

            {story.body.map((p, i) => (
              <p key={i} style={{ margin: '0 0 16px', textWrap: 'pretty' }}>{p}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ThemeMosaicBand({ theme, stories, onOpen }) {
  if (!stories.length) return null;

  const patterns = patternForBand(stories.length);

  return (
    <section style={{ background: theme.bg, padding: '72px 32px 80px' }}>
      <div style={{ maxWidth: 1320, margin: '0 auto' }}>
        <header style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'baseline',
          gap: 24,
          paddingBottom: 22,
          marginBottom: 36,
          borderBottom: `2px solid ${theme.accent}`,
        }}>
          <div>
            <h3 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 36,
              lineHeight: 1.1,
              letterSpacing: '-.015em',
              margin: '0 0 10px',
              color: 'var(--fg-1)',
              textWrap: 'balance',
            }}>{theme.label}</h3>
            <p style={{
              fontSize: 16,
              lineHeight: 1.55,
              color: 'var(--grey)',
              margin: 0,
              maxWidth: '64ch',
              textWrap: 'pretty',
            }}>{theme.intro}</p>
          </div>
          <div style={{
            fontSize: 12,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: theme.accent,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            paddingTop: 10,
          }}>
            {stories.length} {stories.length === 1 ? 'story' : 'stories'}
          </div>
        </header>

        <div className="ssi-wsd__grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gap: 18,
          gridAutoFlow: 'dense',
        }}>
          {stories.map((s, i) => (
            <MosaicCard
              key={s.slug}
              story={s}
              pattern={patterns[i]}
              onOpen={() => onOpen(s)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function EmmaSpotlight({ t }) {
  return (
    <section
      style={{
        background: '#fff',
        padding: '24px 32px 56px',
      }}
    >
      <figure
        style={{
          margin: '0 auto',
          maxWidth: 760,
          background: '#FBF3EF',
          border: '1px solid rgba(73,3,0,.10)',
          borderRadius: 14,
          padding: '36px 40px 30px',
          position: 'relative',
        }}
      >
        <svg
          width="42"
          height="34"
          viewBox="0 0 38 30"
          style={{ position: 'absolute', top: 22, left: 26, opacity: 0.85 }}
          aria-hidden="true"
        >
          <path
            d="M0 30V17.2C0 9.1 2.3 3.3 10.5 0l3.3 3.6c-5 2.6-7 6-7 10.6h7V30H0zm21 0V17.2C21 9.1 23.3 3.3 31.5 0l3.3 3.6c-5 2.6-7 6-7 10.6H35V30H21z"
            fill="#C2130C"
          />
        </svg>
        <blockquote
          style={{
            margin: 0,
            paddingLeft: 56,
            fontFamily: 'var(--font-display-serif, var(--font-display))',
            fontSize: 22,
            lineHeight: 1.45,
            color: 'var(--fg-1)',
            textWrap: 'pretty',
            fontStyle: 'italic',
          }}
        >
          {t.emmaQuote}
        </blockquote>
        <figcaption
          style={{
            marginTop: 22,
            paddingLeft: 56,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: 'var(--brandRed)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 15,
            }}
            aria-hidden="true"
          >
            {(t.emmaName || '?').slice(0, 1)}
          </div>
          <div>
            <div
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 600,
                fontSize: 16,
                color: 'var(--fg-1)',
              }}
            >
              {t.emmaName}
            </div>
            <div style={{ fontSize: 13, color: 'var(--grey)', marginTop: 2 }}>
              {t.emmaRole}
            </div>
          </div>
        </figcaption>
      </figure>
    </section>
  );
}

export function WrittenStoriesD({ t = {} }) {
  const stories = LEARNER_STORIES;
  const [activeStory, setActiveStory] = useState(null);

  return (
    <section>
      <header style={{
        textAlign: 'center',
        maxWidth: 760,
        margin: '0 auto',
        padding: '72px 32px 36px',
      }}>
        <div style={{
          fontSize: 12,
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          color: 'var(--brandRed)',
          fontWeight: 700,
          marginBottom: 10,
        }}>In their own words</div>
        <h2 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 34,
          lineHeight: 1.15,
          margin: '0 0 12px',
          letterSpacing: '-.01em',
          textWrap: 'balance',
        }}>Why people start.</h2>
        <p style={{
          fontSize: 15,
          lineHeight: 1.55,
          color: 'var(--grey)',
          margin: '0 auto',
          maxWidth: '62ch',
          textWrap: 'balance',
        }}>
          Every learner arrives by their own route. Find the one that sounds
          like you, then tap any quote to read the whole story.
        </p>
      </header>

      {t.emmaQuote && <EmmaSpotlight t={t} />}

      {D_THEMES.map(theme => (
        <ThemeMosaicBand
          key={theme.id}
          theme={theme}
          stories={stories.filter(s => s.theme === theme.id)}
          onOpen={setActiveStory}
        />
      ))}

      {activeStory && (
        <StoryModal story={activeStory} onClose={() => setActiveStory(null)} />
      )}
    </section>
  );
}
